<template>
  <div class="admin-tab-dashboard-groups">
    <div v-if="userType === 'admin'">
      <bdg-calendar-view></bdg-calendar-view>
    </div>
    <template v-else>
      <div class="flex flex-row">
        <bdg-group-tab
          :class="{ selected: selectedTab === tab }"
          v-for="(tab, index) in tabs"
          :key="index"
          :icon-class-name="tab.iconClass"
          :tab-text="tab.name"
          @click.native="selectedTab = tab"
        />
      </div>
      <bdg-table-view
        :rows="rows"
        :columns="columns"
        :page-options="{
          isPaged: rows && rows.length > 10,
          itemsPerPage: 10
        }"
        v-bind.sync="tableProps"
      >
        <template #beside-pagination>
          <bdg-button
            class="ghost has-icon uppercase"
            before-icon-class="fal fa-plus-square"
            @click.native="addCrewToSchedule"
            v-if="$mq !== 'sm' && rows === scheduledCrews"
          >
            Add Crew To Schedule
          </bdg-button>
        </template>
      </bdg-table-view>
    </template>
  </div>
</template>

<script>
/* eslint-disable no-case-declarations */
import BdgGroupTab from "../../elements/BdgGroupTab";
import BdgTableView from "../../elements/table-view/BdgTableView";
import BdgCalendarView from "@/elements/calendar-view/BdgCalendarView";
import BdgButton from "../../elements/BdgButton";
import {
  getOnHoldAndCancelledDeliveriesColumns,
  getScheduledCrewsColumns,
  getScheduledDeliveriesAndDeliveredProcurementsColumns
} from "../../js-data/admin-dashboard-columns";
import {
  CalendarTypes,
  CompletionStatusLabels,
  DatetimeGroupByOptions,
  TableRowGroupTypes
} from "../../constants/enums";
import { getMyTasksAndWorkOrdersColumns } from "../../js-data/crew-leader-dashboard-columns";
import AddCrewToScheduleForm from "../../forms/AddCrewToScheduleForm";
import { CompletionStatusIcons } from "../../js-data/icon-maps";
import { UserTypes } from "../../constants/user-enums";

export default {
  name: "DesktopDashboardData",
  components: { BdgButton, BdgTableView, BdgGroupTab, BdgCalendarView },
  data() {
    return {
      selectedTab: null,
      myTasksColumns: getMyTasksAndWorkOrdersColumns("tasks"),
      workOrdersColumns: getMyTasksAndWorkOrdersColumns("work-orders"),
      scheduledCrewsColumns: getScheduledCrewsColumns(this),
      scheduledDeliveriesColumns: getScheduledDeliveriesAndDeliveredProcurementsColumns(
        "scheduled-deliveries",
        this
      ),
      deliveredProcurementsColumns: getScheduledDeliveriesAndDeliveredProcurementsColumns(
        "delivered-procurements"
      ),
      onHoldAndCancelledDeliveriesColumns: getOnHoldAndCancelledDeliveriesColumns()
    };
  },
  computed: {
    userType() {
      return this.$store.state.userType;
    },
    selectedCalendarType() {
      return this.$store.state.selectedCalendarType;
    },
    selectedDate() {
      return this.$store.state.selectedDate;
    },
    selectedWeekStartDate() {
      return this.$store.state.selectedWeekStartDate;
    },
    selectedMonthStartDate() {
      return this.$store.state.selectedMonthStartDate;
    },
    tabs() {
      // Admin tabs
      if (
        this.userType === "admin" &&
        this.$store.state.Dashboard.adminDashboardTabs
      ) {
        return this.$store.state.Dashboard.adminDashboardTabs;
      }
      // Crew Leader tabs
      else if (
        this.userType === UserTypes.CREW_LEADER &&
        this.$store.state.Dashboard.crewLeaderTabs
      ) {
        return this.$store.state.Dashboard.crewLeaderTabs;
      }
      // Designer tabs
      else if (
        (this.userType === UserTypes.CREW_SUPERVISOR ||
          this.userType === UserTypes.DESIGNER) &&
        this.$store.state.Dashboard.crewSupervisorDesignerTabs
      ) {
        return this.$store.state.Dashboard.crewSupervisorDesignerTabs;
      }

      return [];
    },
    myTasks() {
      let tasks = this.$store.state.Dashboard.myTasks;
      if (tasks) {
        tasks.sort(
          (a, b) => a.scheduled_for.getTime() - b.scheduled_for.getTime()
        );

        tasks = tasks.map(t => {
          const nestedFiles = {
            files: t.files.map(f => {
              return {
                ...f,
                viewClickAction: () => {
                  const win = window.open(f.url);
                  win.focus();
                },
                deleteClickAction: () => {
                  const formData = Object.assign({}, t);
                  delete formData.nestedFiles;
                  formData.files.splice(t.files.indexOf(f), 1);

                  this.$store.dispatch("updateContractScopeItemTask", formData);
                }
              };
            })
          };
          return {
            ...t,
            projectNameAndContractPlace: t.projectNameAndContractPlace,
            presentLabelsAndTexts: t.presentLabelsAndTexts,
            nestedFiles,
            nestedTask: { task: t },
            path: t.path,
            statusWithIcon: {
              text: CompletionStatusLabels[t.status],
              iconClasses: `${
                CompletionStatusIcons[t.status]
              } text-xl text-dark-gray`
            }
          };
        });
      }
      return tasks;
    },
    workOrders() {
      let workOrders = this.$store.state.Dashboard.workOrders;
      if (workOrders) {
        workOrders.sort(
          (a, b) =>
            new Date(a.scheduled_for).getTime() -
            new Date(b.scheduled_for).getTime()
        );

        workOrders = workOrders.map(wo => {
          const nestedFiles = {
            files: wo.files.map(f => {
              return {
                ...f,
                viewClickAction: () => {
                  const win = window.open(f.url);
                  win.focus();
                },
                deleteClickAction: () => {
                  const formData = Object.assign({}, wo);
                  delete formData.nestedFiles;
                  formData.files.splice(wo.files.indexOf(f), 1);

                  this.$store.dispatch("updateWorkOrder", formData);
                }
              };
            })
          };
          return {
            ...wo,
            projectNameAndContractPlace: wo.projectNameAndContractPlace,
            presentLabelsAndTexts: wo.presentLabelsAndTexts,
            nestedFiles,
            nestedWorkOrder: { workOrder: wo },
            statusWithIcon: {
              text: CompletionStatusLabels[wo.status],
              iconClasses: `${
                CompletionStatusIcons[wo.status]
              } text-xl text-dark-gray`
            }
          };
        });
      }
      return workOrders;
    },
    isGettingScheduledCrews() {
      return this.$store.state.Dashboard.isGettingScheduledCrews;
    },
    scheduledCrews() {
      let tasks = this.$store.state.Dashboard.scheduledCrews;
      if (tasks) {
        tasks.sort(
          (a, b) =>
            a.scheduled_for.getTime() - b.scheduled_for.getTime() ||
            a.name.localeCompare(b.name)
        );
      }
      return tasks;
    },
    scheduledDeliveries() {
      const procurements = this.$store.state.Dashboard.scheduledDeliveries;
      if (procurements) {
        procurements.sort(
          (a, b) =>
            new Date(a.scheduled_for).getTime() -
              new Date(b.scheduled_for).getTime() ||
            a.name.localeCompare(b.name)
        );

        return procurements.map(proc => {
          proc.scheduled_time = this.$moment(proc.scheduled_for).format(
            "hh:mm a"
          );

          return proc;
        });
      }
      return procurements;
    },
    isGettingDeliveredProcurements() {
      return this.$store.state.Dashboard.isGettingDeliveredProcurements;
    },
    deliveredProcurements() {
      const procurements = this.$store.state.Dashboard.deliveredProcurements;
      if (procurements) {
        procurements.sort(
          (a, b) =>
            new Date(a.delivered_at).getTime() -
              new Date(b.delivered_at).getTime() || a.name.localeCompare(b.name)
        );

        return procurements.map(proc => {
          proc.scheduled_time = this.$moment(proc.scheduled_for).format(
            "hh:mm a"
          );

          return proc;
        });
      }
      return procurements;
    },
    isGettingOnHoldDeliveries() {
      return this.$store.state.Dashboard.isGettingOnHoldDeliveries;
    },
    onHoldDeliveries() {
      const procurements = this.$store.state.Dashboard.onHoldDeliveries;
      if (procurements) {
        procurements.sort(
          (a, b) =>
            new Date(a.scheduled_for).getTime() -
              new Date(b.scheduled_for).getTime() ||
            a.name.localeCompare(b.name)
        );
      }
      return procurements;
    },
    isGettingCancelledDeliveries() {
      return this.$store.state.Dashboard.isGettingCancelledDeliveries;
    },
    cancelledDeliveries() {
      const procurements = this.$store.state.Dashboard.cancelledDeliveries;
      if (procurements) {
        procurements.sort(
          (a, b) =>
            new Date(a.scheduled_for).getTime() -
              new Date(b.scheduled_for).getTime() ||
            a.name.localeCompare(b.name)
        );
      }
      return procurements;
    },
    columns() {
      if (this.selectedTab) {
        switch (this.selectedTab.slug) {
          // Crew Leader tabs
          case "my-tasks":
            return this.myTasksColumns;
          case "work-orders":
            return this.workOrdersColumns;
          // Admin tab
          case "crews-scheduled":
            return this.scheduledCrewsColumns;
          // Shared tabs
          case "scheduled-deliveries":
            let columns = this.scheduledDeliveriesColumns;
            if (this.userType === "admin") {
              const indexOfActions = columns.findIndex(col =>
                col.hasOwnProperty("actions")
              );
              if (indexOfActions > -1) {
                columns.splice(indexOfActions, 1);
              }
            }
            return columns;
          case "delivered-procurements":
            return this.deliveredProcurementsColumns;
          // Admin tab & shared tab
          case "on-hold-deliveries":
          case "cancelled-deliveries":
            return this.onHoldAndCancelledDeliveriesColumns;
          default:
            return null;
        }
      }
      return null;
    },
    rowDataUpdater() {
      if (this.selectedTab) {
        switch (this.selectedTab.slug) {
          case "my-tasks":
            return () => this.$store.dispatch("getMyTasks");
          case "work-orders":
            return () => this.$store.dispatch("getWorkOrders");
          case "crews-scheduled":
            return () => this.$store.dispatch("getScheduledCrews");
          case "scheduled-deliveries":
            return () => this.$store.dispatch("getScheduledDeliveries");
          case "delivered-procurements":
            return () => this.$store.dispatch("getDeliveredProcurements");
          case "on-hold-deliveries":
            return () => this.$store.dispatch("getOnHoldDeliveries");
          case "cancelled-deliveries":
            return () => this.$store.dispatch("getCancelledDeliveries");
          default:
            return null;
        }
      }
      return null;
    },
    rows() {
      let rows = null;
      if (this.selectedTab) {
        switch (this.selectedTab.slug) {
          case "my-tasks":
            rows = this.myTasks;
            break;
          case "work-orders":
            rows = this.workOrders;
            break;
          case "crews-scheduled":
            rows = this.scheduledCrews;
            break;
          case "scheduled-deliveries":
            rows = this.scheduledDeliveries;
            break;
          case "delivered-procurements":
            rows = this.deliveredProcurements;
            break;
          case "on-hold-deliveries":
            rows = this.onHoldDeliveries;
            break;
          case "cancelled-deliveries":
            rows = this.cancelledDeliveries;
            break;
          default:
            break;
        }
      }
      return rows;
    },
    tableProps() {
      if (this.selectedTab) {
        if (
          this.selectedCalendarType === CalendarTypes.DAY &&
          this.selectedTab.slug === "scheduled-deliveries"
        ) {
          return {
            areRowsGrouped: true,
            rowsGroupingParams: {
              groupType: TableRowGroupTypes.DATETIME,
              groupBy: DatetimeGroupByOptions.HOUR,
              groupByProperty: "scheduled_for"
            }
          };
        } else if (this.selectedCalendarType !== CalendarTypes.DAY) {
          switch (this.selectedTab.slug) {
            case "my-tasks":
            case "work-orders":
            case "crews-scheduled":
            case "scheduled-deliveries":
            case "on-hold-deliveries":
            case "cancelled-deliveries":
              return {
                areRowsGrouped: true,
                rowsGroupingParams: {
                  groupType: TableRowGroupTypes.DATETIME,
                  groupBy: DatetimeGroupByOptions.DAY,
                  groupByProperty: "scheduled_for"
                }
              };
            case "delivered-procurements":
              return {
                areRowsGrouped: true,
                rowsGroupingParams: {
                  groupType: TableRowGroupTypes.DATETIME,
                  groupBy: DatetimeGroupByOptions.DAY,
                  groupByProperty: "delivered_at"
                }
              };
            default:
              return undefined;
          }
        }
      }
      return undefined;
    }
  },
  watch: {
    selectedCalendarType(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.rowDataUpdater();
      }
    },
    selectedDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.rowDataUpdater();
      }
    },
    selectedWeekStartDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.rowDataUpdater();
      }
    },
    selectedMonthStartDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.rowDataUpdater();
      }
    },
    selectedTab(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.rowDataUpdater();
      }
    }
  },
  created() {
    if (this.tabs && this.tabs.length) {
      this.selectedTab = this.tabs[0];
    }
  },
  methods: {
    addCrewToSchedule() {
      this.$store.commit("setCreateEditComponentParams", {
        createEditComponent: AddCrewToScheduleForm,
        createEditComponentIsShown: true
      });
    }
  }
};
</script>

<style scoped></style>
